import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { DrawerProps } from './Drawer.types';
import './Drawer.scss';

export default function Drawer({
  open, onClose, side = 'right', title, children, footer, width = 380, closeOnOverlay = true, className, ariaLabel,
}: DrawerProps) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => {
      const node = ref.current;
      if (!node) return;
      const focusable = node.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      (focusable ?? node).focus();
    }, 10);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div
      className={['drawer', `drawer--${side}`, className || ''].filter(Boolean).join(' ')}
      onMouseDown={e => { if (closeOnOverlay && e.target === e.currentTarget) onClose(); }}
    >
      <div className="drawer__overlay" aria-hidden="true" />
      <aside
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
        tabIndex={-1}
        style={{ width: `${width}px` }}
        className="drawer__panel"
      >
        {title ? (
          <div className="drawer__header">
            <h2 id={titleId} className="drawer__title">{title}</h2>
            <button type="button" aria-label="Закрыть" className="drawer__close" onClick={onClose}><X size={18} /></button>
          </div>
        ) : (
          <button type="button" aria-label="Закрыть" className="drawer__close drawer__close--floating" onClick={onClose}><X size={18} /></button>
        )}
        <div className="drawer__body">{children}</div>
        {footer ? <div className="drawer__footer">{footer}</div> : null}
      </aside>
    </div>,
    document.body,
  );
}
